<script setup lang="ts">
import { checkVisibilityAccosBarMini, getPredefinedSuggestionsOnFocus, redirectToFilterPage, stickyAccosBar } from '@/composables/accosbar.helpers'
import { AREAID, CULTURE, ENABLEMINIACCOSBAR, PID, THEMEID, toFormattedRoomStringLong, toFormattedStringDateShort } from '@/composables/helpers'
import type { IAccosBarDto } from '@/composables/interfaces/apiInterfaces'
import { useAccosBarApi } from '@/composables/useApi'
import { loadAccosBarMini } from '@/composables/windowSize'
import { AccosBarDataQueryString } from '@/constants'
import t from '@/i18n/translations'
import { areas, location, popupVisible, rooms } from '@/store/accosbar.store'
import { filterStore } from '@/store/filter.store'
import type { AutoCompleteCompleteEvent } from 'primevue/autocomplete'
import AutoComplete from 'primevue/autocomplete'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import InputText from 'primevue/inputtext'
import { computed, onMounted, ref } from 'vue'
import CloseSvg from '../svgs/CloseSvg.vue'
import TravelDataPopUp from '../traveldatapopup/TravelDataPopUp.vue'
import AccosBarMini from './AccosBarMini.vue'

const filteredLocation = ref<Array<{ areaId: number; name: string }>>([])
const formattedDateString = computed(() => toFormattedStringDateShort())
const accosBarMiniVisible = ref(false)
const formattedRoomString = computed(() => toFormattedRoomStringLong(rooms.value, CULTURE))

function visibilityAccosBarMini(): void {
  const accosBar = document.querySelector('.accosbar')
  const accosBarMini = document.querySelector('.accosbar-mini')
  accosBarMiniVisible.value = checkVisibilityAccosBarMini(accosBar, accosBarMini)
}

function visibilityAccosBar(): void {
  const accosBar = document.querySelector('.accosbar')
  stickyAccosBar(accosBar)
}

function searchLocation(event: AutoCompleteCompleteEvent): void {
  if (areas.value != null) {
    const predefinedSuggestionsOnFocus = getPredefinedSuggestionsOnFocus(PID)
    if (event.originalEvent.type == 'focus' && location.value == null && predefinedSuggestionsOnFocus != null) {
      filteredLocation.value = predefinedSuggestionsOnFocus
    } else {
      if (!event.query.trim().length) {
        filteredLocation.value = [...areas.value]
      } else {
        filteredLocation.value = areas.value.filter((country: IAccosBarDto) => {
          return country.name.toLowerCase().startsWith(event.query.toLowerCase())
        })
      }
    }
  }
}

function clearLocationValue(): void {
  location.value = null
}

onMounted(async () => {
  const { data, error } = await useAccosBarApi()
  if (!error) areas.value = data

  if (AREAID != null) {
    const url = new URL(window.location.toString())
    if (url.searchParams.get(AccosBarDataQueryString) == null) {
      const locationName = areas.value?.find((loc: IAccosBarDto) => loc.areaId === AREAID)
      location.value = { areaId: AREAID, name: locationName?.name }
    }
  }

  //needed if comming from accoslistpage and there a areaid is set
  if (filterStore.value.areaId != null && filterStore.value.areaId != 0 && AREAID == null && location.value?.areaId == null) {
    const locationName = areas.value?.find((loc: IAccosBarDto) => loc.areaId === filterStore.value.areaId)
    location.value = { areaId: filterStore.value.areaId, name: locationName?.name }
  }

  if (THEMEID != null) filterStore.value.themeId = THEMEID // saving to local storage like this
})

window.addEventListener('scroll', visibilityAccosBar)
window.addEventListener('scroll', visibilityAccosBarMini)
</script>

<template>
  <div class="accosbar-base md:relative md:min-h-20">
    <div class="accosbar" sticky-accosbar=".accosbar-base">
      <div class="container mx-auto">
        <div class="pui-accosbar w-full rounded-lg p-2.5">
          <div class="accosbar-inner flex flex-col items-center justify-center gap-2 md:flex-row">
            <div class="relative h-11 w-full md:h-12">
              <IconField class="flex h-full">
                <AutoComplete
                  v-model="location"
                  class="w-full"
                  optionLabel="name"
                  :suggestions="filteredLocation"
                  @complete="searchLocation"
                  :complete-on-focus="true"
                  :placeholder="t(`${CULTURE}.accosbar.location`)"
                  :fluid="true"
                  forceSelection
                  :panelClass="'bg-white'"
                  :inputClass="'text-[var(--p-text-color)] md:text-lg'"
                >
                  <template #option="slotProps">
                    <div>
                      <div class="text-[var(--p-text-color)] md:text-lg">{{ slotProps.option.name }}</div>
                    </div>
                  </template>
                </AutoComplete>
                <InputIcon v-if="!location">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="var(--p-text-color)" class="size-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                  </svg>
                </InputIcon>
              </IconField>
              <button v-if="location" @click="clearLocationValue" class="absolute right-2 top-1/2 z-50 -translate-y-1/2 text-gray-500 hover:text-black">
                <CloseSvg />
              </button>
            </div>
            <div class="relative h-11 w-full md:h-12">
              <IconField class="flex h-full">
                <InputText
                  v-model="formattedDateString"
                  :readonly="true"
                  :fluid="true"
                  :placeholder="t(`${CULTURE}.accosbar.date`)"
                  @click="popupVisible = true"
                  class="cursor-pointer text-[var(--p-text-color)] md:text-lg"
                />
                <InputIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="var(--p-text-color)" class="size-5">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                    />
                  </svg>
                </InputIcon>
              </IconField>
            </div>
            <div class="relative h-11 w-full md:h-12">
              <IconField class="flex h-full">
                <InputText
                  :model-value="formattedRoomString"
                  :readonly="true"
                  :fluid="true"
                  :placeholder="t(`${CULTURE}.accosbar.occupancy`)"
                  class="cursor-pointer text-[var(--p-text-color)] md:text-lg"
                  @click="popupVisible = true"
                />
                <InputIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="var(--p-text-color)" class="size-5">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                    />
                  </svg>
                </InputIcon>
              </IconField>
            </div>
            <button type="submit" class="myEnquiryButton h-11 w-full rounded px-4 py-2 md:h-12 md:w-fit md:text-xl" @click="redirectToFilterPage()">
              {{ t(`${CULTURE}.accosbar.search`) }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <TravelDataPopUp />
  <AccosBarMini v-if="loadAccosBarMini() && accosBarMiniVisible && ENABLEMINIACCOSBAR" />
</template>
